import React, { useState, useRef, useEffect } from 'react';
import { message } from 'antd';
import agentActions from '../../../../actions/agent';
import Modal from '../../../../components/Modal';
import BrandDropdown, { BrandChip } from './BrandDropdown';


export default function ThreadSidebar({
  brands = [],
  selectedBrandId,
  onSelectBrand,
  selectedThreadId,
  navigate,
  onClose,
  isMobile,
}) {
  // Threads state — managed internally
  const [threads, setThreads] = useState([]);

  // New chat modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalBrandId, setModalBrandId] = useState('');
  const [modalName, setModalName] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const nameInputRef = useRef(null);

  // Load threads when selected brand changes
  useEffect(() => {
    if (!selectedBrandId) return;
    setLoadingThreads(true);
    agentActions.listThreads({ brandId: selectedBrandId })
      .then(res => setThreads(res?.success ? res.data || [] : []))
      .catch(() => setThreads([]))
      .finally(() => setLoadingThreads(false));
  }, [selectedBrandId]);

  // If the active thread isn't in list (e.g. auto-created), reload
  useEffect(() => {
    if (!selectedBrandId || !selectedThreadId) return;
    if (threads.find(t => t.id === selectedThreadId)) return;
    agentActions.listThreads({ brandId: selectedBrandId })
      .then(res => setThreads(res?.success ? res.data || [] : []))
      .catch(() => {});
  }, [selectedThreadId, selectedBrandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = () => {
    setModalBrandId(selectedBrandId || brands[0]?.brand_id || '');
    setModalName('');
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!modalBrandId) return;
    setCreating(true);
    try {
      const brand = brands.find(b => b.brand_id === modalBrandId);
      const res = await agentActions.createThread({
        brand_id: modalBrandId,
        brand_name: brand?.brand_name,
        title: modalName.trim() || undefined,
      });
      if (res?.success) {
        if (modalBrandId !== selectedBrandId) onSelectBrand(modalBrandId);
        setThreads(prev => [res.data, ...prev]);
        navigate(`/agent/${res.data.id}`);
        setModalOpen(false);
      }
    } catch (err) {
      message.error(err?.error || 'Failed to create thread');
    } finally { setCreating(false); }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await agentActions.deleteThread(threadId);
      setThreads(prev => prev.filter(t => t.id !== threadId));
      if (selectedThreadId === threadId) navigate('/agent');
      message.success('Conversation deleted');
    } catch (_) {
      message.error('Failed to delete conversation');
    }
  };

  const getBrandForThread = (thread) => {
    const bId = thread.metadata?.brand_id || thread.brand_id;
    return brands.find(b => b.brand_id === bId);
  };

  return (
    <>
      <div style={{
        width: isMobile ? '100%' : 260,
        minWidth: isMobile ? '100%' : 260,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(5,5,5,0.98)',
        borderRight: '1px solid rgba(255,255,255,0.08)',
        position: isMobile ? 'fixed' : 'relative',
        top: 0, left: 0,
        zIndex: isMobile ? 100 : 'auto',
      }}>
        {/* ── Header ───────────────────────────────────────── */}
        <div style={{
          padding: '14px 14px 12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 26, height: 26, borderRadius: 7,
              background: 'linear-gradient(135deg, rgba(255,126,103,0.2), rgba(255,77,141,0.18))',
              border: '1px solid rgba(255,126,103,0.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="rgba(255,126,103,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF', letterSpacing: '-0.2px' }}>
              AI Agent
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Compose / New Chat icon */}
            <button
              onClick={openModal}
              title="New conversation"
              style={{
                background: 'none', border: '1px solid transparent', borderRadius: 7,
                color: 'rgba(255,255,255,0.4)', width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0, transition: 'all 140ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#FF7E67';
                e.currentTarget.style.background = 'rgba(255,126,103,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,126,103,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255,255,255,0.4)';
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              {/* Compose/pencil icon */}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </button>

            {isMobile && (
              <button
                onClick={onClose}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.35)', width: 28, height: 28,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 6, padding: 0,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Brand Filter Dropdown ─────────────────────────── */}
        <div style={{ padding: '10px 12px 8px', flexShrink: 0 }}>
          <BrandDropdown brands={brands} selectedBrandId={selectedBrandId} onSelect={onSelectBrand} />
        </div>

        {/* ── Section label ────────────────────────────────── */}
        <div style={{ padding: '6px 14px 6px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            Conversations
          </span>
          {threads.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '1px 6px' }}>
              {threads.length}
            </span>
          )}
        </div>

        {/* ── Thread list ──────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 6px 8px' }}>
          {loadingThreads ? (
            <div style={{ padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ height: 52, borderRadius: 10, background: 'rgba(255,255,255,0.03)', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)', animation: 'shimmer 1.5s ease infinite' }} />
                </div>
              ))}
            </div>
          ) : threads.length === 0 ? (
            <div style={{ padding: '20px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', lineHeight: 1.6 }}>
                No conversations yet.<br />
                <button
                  onClick={openModal}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,126,103,0.7)', cursor: 'pointer', fontSize: 12, padding: 0, marginTop: 4, fontFamily: 'inherit' }}
                >
                  Start one ↗
                </button>
              </div>
            </div>
          ) : (
            threads.map(thread => {
              const isActive = thread.id === selectedThreadId;
              const brand = getBrandForThread(thread);
              return (
                <ThreadRow
                  key={thread.id}
                  thread={thread}
                  brand={brand}
                  isActive={isActive}
                  onSelect={() => { navigate(`/agent/${thread.id}`); if (isMobile) onClose?.(); }}
                  onDelete={() => handleDeleteThread(thread.id)}
                />
              );
            })
          )}
        </div>
      </div>

      {/* ── New Chat Modal ───────────────────────────────────────── */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} width={380} initialFocusRef={nameInputRef}>
        <Modal.Header title="New Conversation" onClose={() => setModalOpen(false)} />
        <Modal.Body style={{ padding: '16px 18px 18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Brand selector */}
            <div>
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 7 }}>
                Brand
              </label>
              <BrandDropdown brands={brands} selectedBrandId={modalBrandId} onSelect={setModalBrandId} placeholder="Select brand..." />
            </div>
            {/* Name input */}
            <div>
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 7 }}>
                Name <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.6 }}>(optional)</span>
              </label>
              <input
                ref={nameInputRef}
                value={modalName}
                onChange={e => setModalName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="e.g. Instagram campaign ideas…"
                style={{
                  width: '100%', padding: '8px 11px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, color: '#FFFFFF',
                  fontSize: 13, outline: 'none', fontFamily: 'inherit',
                  boxSizing: 'border-box', transition: 'border-color 140ms ease',
                }}
                onFocus={e => { e.target.style.borderColor = 'rgba(255,126,103,0.4)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setModalOpen(false)}
            className="agent-btn-ghost"
            style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
          >Cancel</button>
          <button
            onClick={handleCreate}
            disabled={!modalBrandId || creating}
            style={{
              padding: '7px 18px', borderRadius: 8, border: 'none',
              background: modalBrandId && !creating ? 'linear-gradient(to right, #FF7E67, #FF4D8D)' : 'rgba(255,255,255,0.08)',
              color: modalBrandId && !creating ? '#fff' : 'rgba(255,255,255,0.25)',
              fontSize: 13, fontWeight: 600, cursor: modalBrandId && !creating ? 'pointer' : 'default',
              fontFamily: 'inherit',
              boxShadow: modalBrandId && !creating ? '0 2px 12px rgba(255,126,103,0.35)' : 'none',
            }}
          >{creating ? 'Creating…' : 'Create'}</button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

function ThreadRow({ thread, brand, isActive, onSelect, onDelete }) {
  const [hov, setHov] = useState(false);
  const timeAgo = formatRelativeTime(thread.updatedAt || thread.createdAt);

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onSelect(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px',
        borderRadius: 10,
        cursor: 'pointer',
        background: isActive ? 'rgba(255,126,103,0.08)' : hov ? 'rgba(255,255,255,0.04)' : 'transparent',
        border: `1px solid ${isActive ? 'rgba(255,126,103,0.2)' : 'transparent'}`,
        marginBottom: 2,
        transition: 'all 120ms ease',
      }}
    >
      {/* Brand logo / chip */}
      {brand?.logo_url ? (
        <img src={brand.logo_url} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
      ) : brand ? (
        <BrandChip name={brand.brand_name} size={22} />
      ) : (
        <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
      )}

      {/* Thread info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: isActive ? 600 : 400,
          color: isActive ? '#FFFFFF' : hov ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.45)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3,
          transition: 'color 120ms ease',
        }}>
          {thread.title || 'New conversation'}
        </div>
        {timeAgo && (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', marginTop: 1 }}>
            {timeAgo}
          </div>
        )}
      </div>

      {/* Delete */}
      {(isActive || hov) && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete conversation"
          style={{
            background: 'none', border: 'none', padding: 6, flexShrink: 0,
            width: 26, height: 26,
            color: 'rgba(255,255,255,0.2)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, transition: 'all 120ms ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#ff6b6b'; e.currentTarget.style.background = 'rgba(255,107,107,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)'; e.currentTarget.style.background = 'none'; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}
